import { X } from 'lucide-react';
import type {
  ArkTaskParameterVocabularyId,
  ArkTaskProtocolParameter,
  CreateProtocolRequest,
  MeteringPricedDescriptorField,
  MeteringProfileKind,
  MeteringSpec,
  MeteringVariantField,
  ModelProtocol,
  ModelProtocolRecord,
  ProfileMeteringCapability,
} from '@nadoutong/contracts';
import {
  VIDEO_INPUT_VARIANT_VALUES,
  arkTaskParametersForVocabulary,
  builtinUnitMeteringSpec,
  isArkTaskIngressProfileKind,
  isUnitMeteredProfileKind,
  meteringCapabilityForProfileKind,
  protocolsAdmittingMeteringProfile,
  validateArkTaskAdmissionSurface,
  validateMeteringSpec,
  variantFieldsInTemplate,
} from '@nadoutong/contracts';
import { useT } from '../i18n/index.ts';
import type { I18nKey } from '../i18n/index.ts';
import {
  Button,
  FormField,
  Input,
  Select,
} from '../ui/index.ts';
import { Textarea } from '../ui/index.ts';
import { MeteringShapeSummary } from './VariantKeyFields.tsx';

/**
 * The configuration documents of one protocol registration (#1883, parent
 * #1838): the metering spec and the task admission surface, as a FORM derived
 * from the selected profile's capability row.
 *
 * ── STRUCTURE FOLLOWS THE CAPABILITY TABLE ────────────────────────────────
 *
 * `PROFILE_METERING_CAPABILITY` decides what appears:
 *   - token lane, no fixed template (every plain SKU): nothing but the
 *     read-only shape line — no legal spec exists, so no control is offered.
 *   - token lane with a FIXED template (`ark_video_token_v1`): NO control at all.
 *     The owner's 2026-08-16 verdict: the profile IS the sales form, its
 *     tier axis included, so the `metering_spec` is GENERATED from
 *     `fixedVariantKeyTemplate` on every registration. The read-only shape
 *     line states the fixed form.
 *   - unit lane: the structured algebra (coefficients, clamps, per-variant
 *     tables) is a document editor problem, so it keeps a raw JSON editor —
 *     validated with the SAME contracts validators the write face refuses
 *     with, so a 400 is met here as a readable message before anything is
 *     posted.
 *   - a task-ingress profile additionally shows "what is accepted": the ark
 *     protocol's parameter VOCABULARY as a READ-ONLY table (names, types and
 *     prompt-command spellings come from the vocabulary version this SKU
 *     sells — V1 unless `task_admission.parameter_vocabulary` is `"v2"`;
 *     #1883 retired the operator-authored parameter surface; value domains
 *     are the upstream's own and billable values are the price matrix's),
 *     followed by the concurrency ADMISSION editor, whose tier classes are
 *     the allowance map's own keys.
 *
 * ── FULL RE-SEND, THE #1944 CLOSURE ───────────────────────────────────────
 *
 * Registration is a whole-row UPSERT: what the form does not send is
 * CLEARED. `protocolDocsFrom` therefore backfills every stored document into
 * the draft, and re-registration re-sends all of them. A fixed-template
 * token spec is re-GENERATED rather than re-sent, which also converges a
 * legacy stored template onto the profile's fixed one on the next
 * re-registration. `task_parameter_surface` is deliberately NEVER sent —
 * the platform write face 400s the field by name as retired.
 */

/** One allowance row: a tier class (the map's own key) and its in-flight cap. */
export interface AdmissionAllowanceDraft {
  readonly tierClass: string;
  readonly allowance: string;
}

export const EMPTY_ALLOWANCE: AdmissionAllowanceDraft = { tierClass: '', allowance: '1' };

export interface ProtocolDocsDraft {
  /** Carried through from a backfilled token spec, so re-send is faithful. */
  readonly specProtocols?: readonly ModelProtocol[];
  /** Unit-lane raw spec JSON; empty means "store no spec". */
  readonly specJson: string;
  readonly tierClassParameter: string;
  readonly allowances: readonly AdmissionAllowanceDraft[];
  /**
   * Carried through from a stored admission document so a re-save does
   * not silently revert a V2 SKU to V1. There is no control to edit it
   * here: cutting a SKU over is an operator API action, not a form
   * field in this PR.
   */
  readonly parameterVocabulary?: ArkTaskParameterVocabularyId;
}

export function emptyProtocolDocs(): ProtocolDocsDraft {
  return {
    specJson: '',
    tierClassParameter: '',
    allowances: [EMPTY_ALLOWANCE],
  };
}

/** Backfill the draft from a registration's stored documents. */
export function protocolDocsFrom(record: ModelProtocolRecord): ProtocolDocsDraft {
  const spec = record.metering_spec ?? null;
  const admission = record.task_admission ?? null;
  const tokenSpec = spec !== null && spec.lane === 'token' ? spec : null;
  const allowances =
    admission === null
      ? [EMPTY_ALLOWANCE]
      : Object.entries(admission.max_in_flight_by_tier_class).map(([tierClass, value]) => ({
          tierClass,
          allowance: String(value),
        }));
  return {
    ...(tokenSpec?.protocols !== undefined ? { specProtocols: tokenSpec.protocols } : {}),
    specJson:
      spec !== null && spec.lane === 'unit' ? JSON.stringify(spec, null, 2) : '',
    tierClassParameter: admission?.tier_class_parameter ?? '',
    allowances: allowances.length > 0 ? allowances : [EMPTY_ALLOWANCE],
    ...(admission?.parameter_vocabulary === undefined
      ? {}
      : { parameterVocabulary: admission.parameter_vocabulary }),
  };
}

export type ProtocolDocsBuild =
  | {
      readonly ok: true;
      readonly documents: Partial<
        Pick<CreateProtocolRequest, 'metering_spec' | 'task_admission'>
      >;
    }
  | { readonly ok: false; readonly message: string };

type Translate = (key: I18nKey, params?: Record<string, string | number>) => string;

/**
 * Build and validate the registration's document keys, with the SAME
 * contracts validators and capability cross-checks the platform write face
 * refuses with. A plain profile with no documents builds `{}`, so the
 * submitted body of every SKU in service today is byte-identical to before.
 */
export function buildProtocolDocuments(
  protocol: ModelProtocol,
  profile: MeteringProfileKind,
  draft: ProtocolDocsDraft,
  t: Translate,
): ProtocolDocsBuild {
  const capability = meteringCapabilityForProfileKind(profile);
  let spec: MeteringSpec | null = null;

  if (capability.lane === 'token') {
    // A fixed-template profile's spec is GENERATED, never chosen: the profile
    // is the sales form and the template is its fixed tier axis. Every
    // registration of such a profile stores it, so a legacy divergent stored
    // template converges on the fixed one at the next re-registration.
    if (capability.fixedVariantKeyTemplate !== null) {
      const candidate = {
        spec_version: 1,
        lane: 'token',
        ...(draft.specProtocols !== undefined ? { protocols: draft.specProtocols } : {}),
        variant_key_template: capability.fixedVariantKeyTemplate,
      };
      const validated = validateMeteringSpec(candidate);
      if (!validated.ok) {
        return {
          ok: false,
          message: t('model_detail.protocols.docs.error.spec_invalid', {
            violations: validated.violations.join(', '),
          }),
        };
      }
      spec = validated.spec;
    }
  } else {
    const trimmed = draft.specJson.trim();
    if (trimmed.length > 0) {
      let parsed: unknown;
      try {
        parsed = JSON.parse(trimmed);
      } catch {
        return { ok: false, message: t('model_detail.protocols.docs.error.spec_json_invalid') };
      }
      const validated = validateMeteringSpec(parsed);
      if (!validated.ok) {
        return {
          ok: false,
          message: t('model_detail.protocols.docs.error.spec_invalid', {
            violations: validated.violations.join(', '),
          }),
        };
      }
      if (validated.spec.lane !== 'unit') {
        return {
          ok: false,
          message: t('model_detail.protocols.docs.error.spec_lane_mismatch', { profile }),
        };
      }
      // The same fixed-template rule the platform write face refuses with:
      // a declared template must equal the profile's fixed one VERBATIM.
      const declaredTemplate = validated.spec.variant_key_template;
      if (
        declaredTemplate !== undefined &&
        declaredTemplate !== capability.fixedVariantKeyTemplate
      ) {
        return {
          ok: false,
          message: t('model_detail.protocols.docs.error.spec_template_fixed', {
            template: declaredTemplate,
            profile,
            fixed:
              capability.fixedVariantKeyTemplate ??
              t('model_detail.protocols.docs.error.spec_template_fixed_none'),
          }),
        };
      }
      const terms = [
        validated.spec.quantity,
        ...Object.values(validated.spec.quantity_by_variant ?? {}),
      ];
      for (const term of terms) {
        if (term.field !== undefined && !capability.pricedDescriptorFields.includes(term.field)) {
          return {
            ok: false,
            message: t('model_detail.protocols.docs.error.spec_descriptor_not_produced', {
              field: term.field,
              profile,
            }),
          };
        }
      }
      if (validated.spec.surcharge !== undefined) {
        const admitted = capability.surcharge;
        if (admitted === undefined) {
          return {
            ok: false,
            message: t('model_detail.protocols.docs.error.spec_surcharge_not_sold', { profile }),
          };
        }
        if (validated.spec.surcharge.unit !== admitted.unit) {
          return {
            ok: false,
            message: t('model_detail.protocols.docs.error.spec_surcharge_unit', {
              unit: validated.spec.surcharge.unit,
              admitted: admitted.unit,
              profile,
            }),
          };
        }
        const field = validated.spec.surcharge.quantity.field;
        if (field !== undefined && !admitted.pricedDescriptorFields.includes(field)) {
          return {
            ok: false,
            message: t('model_detail.protocols.docs.error.spec_surcharge_descriptor', {
              field,
              profile,
            }),
          };
        }
      }
      if (validated.spec.protocols !== undefined && !validated.spec.protocols.includes(protocol)) {
        return {
          ok: false,
          message: t('model_detail.protocols.docs.error.spec_protocol_excluded', { protocol }),
        };
      }
      spec = validated.spec;
    }
  }

  if (!isArkTaskIngressProfileKind(profile)) {
    return { ok: true, documents: { ...(spec !== null ? { metering_spec: spec } : {}) } };
  }

  const admissionDocument = {
    tier_class_parameter: draft.tierClassParameter,
    max_in_flight_by_tier_class: Object.fromEntries(
      draft.allowances
        .filter((row) => row.tierClass.trim().length > 0)
        .map((row) => [row.tierClass.trim(), Number(row.allowance.trim())]),
    ),
    ...(draft.parameterVocabulary === undefined
      ? {}
      : { parameter_vocabulary: draft.parameterVocabulary }),
  };
  const admissionValidated = validateArkTaskAdmissionSurface(admissionDocument);
  if (!admissionValidated.ok) {
    return {
      ok: false,
      message: t('model_detail.protocols.docs.error.admission_invalid', {
        violations: admissionValidated.violations.join(', '),
      }),
    };
  }

  return {
    ok: true,
    documents: {
      ...(spec !== null ? { metering_spec: spec } : {}),
      task_admission: admissionValidated.admission,
    },
  };
}

/**
 * The spec textarea's example — DERIVED from the profile's capability row, or
 * absent. There is deliberately no third outcome (#2251, #2255).
 *
 * The placeholder is instructions. An operator who pastes it verbatim has done
 * what the console told them to, so an example the write face refuses is a 400
 * the product typed for them. Before this rewrite that is exactly what it was
 * for four of the five profiles that reach here — found by hand while
 * provisioning the first `ark_video_seconds_ref_v1` SKU, not by any test,
 * because the example had its own assertions and `validateMeteringSpec` had its
 * own and neither ever looked at the other.
 *
 * Everything now comes from a table the write face itself enforces:
 *
 *   - `protocols` from {@link protocolsAdmittingMeteringProfile}, the pairing
 *     table read backwards. Required because both seconds descriptors are
 *     protocol-scoped (`descriptor_protocol_undeclared` otherwise), and
 *     hard-coding `ark_video_v1` would be wrong for `grok_video_seconds_v1`,
 *     which shares this code path.
 *   - `quantity` / `unit` from the capability's own `pricedDescriptorFields`.
 *   - `quantity_by_variant` from the fixed template's fields — mutually
 *     required with `variant_key_template` under ADR-012 §4 precondition 1, and
 *     its absence was the second half of the old breakage
 *     (`variant_template_without_coefficients`).
 *   - `surcharge` when, and only when, the capability sells one.
 *
 * **Anything not derivable returns the translated fallback.** A profile whose
 * spec is BUILT IN (`openai_images_unit_v1`) returns it too: its billing is
 * already decided in contracts, so suggesting a document invents a decision the
 * operator does not have to make — and the old code handed that image-billing
 * profile a seconds document, which was not merely illegal but a different
 * product.
 *
 * The tier VALUES are samples and nothing more: a SKU's real tiers are its
 * supply's, which no example can know. The free allowance on a surcharge is the
 * same kind of fact, so the term is emitted neutral (`b: 0`) rather than
 * carrying one vendor's number into every other vendor's SKU.
 */

/**
 * Sample tier values per variant field, for the placeholder only.
 *
 * `has_video_input` takes its two spellings from the contract constant rather
 * than a literal, because those strings are a wire vocabulary the gateway
 * writes and the price table is keyed by — a typo here would produce a legal
 * document whose rows never match a real request.
 */
const SAMPLE_VARIANT_VALUES: Readonly<
  Partial<Record<MeteringVariantField, readonly string[]>>
> = {
  resolution: ['720p', '1080p'],
  has_video_input: [
    VIDEO_INPUT_VARIANT_VALUES.absent,
    VIDEO_INPUT_VARIANT_VALUES.present,
  ],
};

/** The unit a priced descriptor bills in, for the ones an example can serve. */
const DESCRIPTOR_SAMPLE_UNITS: Readonly<
  Partial<Record<MeteringPricedDescriptorField, string>>
> = {
  duration_seconds: 'second',
};

/** Every key the fixed template can spell from the samples, or `null`. */
function sampleVariantKeys(template: string): readonly string[] | null {
  const fields = variantFieldsInTemplate(template);
  if (fields.length === 0) return null;
  let keys: readonly string[] = [template];
  for (const field of fields) {
    const samples = SAMPLE_VARIANT_VALUES[field];
    if (samples === undefined) return null;
    keys = keys.flatMap((key) => samples.map((value) => key.replace(`{${field}}`, value)));
  }
  return keys;
}

export function specJsonExample(
  capability: ProfileMeteringCapability,
  fallback: string,
  profile: MeteringProfileKind,
): string {
  const template = capability.fixedVariantKeyTemplate;
  if (template === null) return fallback;
  // A token-lane profile has no unit document to suggest, and a profile whose
  // unit spec is BUILT IN does not need a stored one at all.
  if (!isUnitMeteredProfileKind(profile)) return fallback;
  if (builtinUnitMeteringSpec(profile) !== null) return fallback;

  const field = capability.pricedDescriptorFields[0];
  const unit = field === undefined ? undefined : DESCRIPTOR_SAMPLE_UNITS[field];
  const keys = sampleVariantKeys(template);
  if (field === undefined || unit === undefined || keys === null) return fallback;

  const term = `{"field":"${field}","a":1,"b":0}`;
  const byVariant = keys.map((key) => `"${key}":${term}`).join(',');
  const protocols = JSON.stringify(protocolsAdmittingMeteringProfile(profile));

  const surchargeField = capability.surcharge?.pricedDescriptorFields[0];
  const surcharge =
    capability.surcharge === undefined || surchargeField === undefined
      ? ''
      : `,"surcharge":{"unit":"${capability.surcharge.unit}",`
        + `"quantity":{"field":"${surchargeField}","a":1,"b":0,"min":0}}`;

  return `{"spec_version":1,"lane":"unit","unit":"${unit}",`
    + `"protocols":${protocols},"quantity":${term},`
    + `"quantity_by_variant":{${byVariant}},`
    + `"variant_key_template":"${template}","delivery_gate":"positive_quantity"${surcharge}}`;
}

// ─── form sections ───────────────────────────────────────────────────────

interface DocsFieldsProps {
  readonly profile: MeteringProfileKind;
  readonly draft: ProtocolDocsDraft;
  readonly onChange: (draft: ProtocolDocsDraft) => void;
  readonly disabled: boolean;
}

export function ProtocolDocsFields({
  profile,
  draft,
  onChange,
  disabled,
}: DocsFieldsProps): JSX.Element {
  const t = useT();
  const capability = meteringCapabilityForProfileKind(profile);
  const isTask = isArkTaskIngressProfileKind(profile);
  const showSpecJson = capability.lane === 'unit';

  const specJsonField = showSpecJson ? (
    <FormField
      label={t('model_detail.protocols.docs.spec_json.label')}
      fieldId="model-protocols-spec-json"
      hint={t('model_detail.protocols.docs.spec_json.hint')}
    >
      <Textarea
        id="model-protocols-spec-json"
        mono
        rows={8}
        value={draft.specJson}
        onChange={(e) => onChange({ ...draft, specJson: e.currentTarget.value })}
        data-testid="model-protocols-spec-json"
        disabled={disabled}
        placeholder={specJsonExample(capability, t('model_detail.protocols.docs.spec_json.placeholder'), profile)}
      />
    </FormField>
  ) : null;

  if (!isTask) {
    return (
      <div className="flex flex-col gap-4" data-testid="model-protocols-docs">
        {/* The billing shape is READ-ONLY: a fixed-template profile's tier
            axis is the profile's own, so there is no "which fields" control —
            the spec is generated from the fixed template at submit. */}
        <MeteringShapeSummary profile={profile} testId="model-protocols-shape" />
        {specJsonField}
      </div>
    );
  }

  // A task registration groups its content under two headed sections (owner
  // review, 2026-08-16): "what is accepted" — since #1883 a READ-ONLY
  // statement of the protocol vocabulary (the parameters, their types and
  // their prompt commands are code, not operator data) plus the concurrency
  // admission editor — before "how it bills" (the profile-fixed billing
  // shape, read-only except the unit lane's spec document).
  return (
    <div className="flex flex-col gap-4" data-testid="model-protocols-docs">
      <section
        className="flex flex-col gap-3"
        data-testid="model-protocols-section-accepts"
      >
        <h3 className="m-0 text-section text-paper">
          {t('model_detail.protocols.docs.section.accepts.title')}
        </h3>
        <p className="m-0 text-micro text-paper-low">
          {t('model_detail.protocols.docs.section.accepts.hint')}
        </p>
        <VocabularyTable
          vocabulary={arkTaskParametersForVocabulary(draft.parameterVocabulary ?? 'v1')}
        />
        <AdmissionEditor draft={draft} onChange={onChange} disabled={disabled} />
      </section>
      <section
        className="flex flex-col gap-3"
        data-testid="model-protocols-section-billing"
      >
        <h3 className="m-0 text-section text-paper">
          {t('model_detail.protocols.docs.section.billing.title')}
        </h3>
        {/* "Nothing to fill in" is only claimed when it is true: the unit
            lane's spec document below IS operator input, so the read-only
            note renders only for the token lane. */}
        {showSpecJson ? null : (
          <p className="m-0 text-micro text-paper-low">
            {t('model_detail.protocols.docs.section.billing.hint')}
          </p>
        )}
        <MeteringShapeSummary profile={profile} testId="model-protocols-shape" />
        {specJsonField}
      </section>
    </div>
  );
}

/**
 * The ark protocol's parameter vocabulary, read-only, straight from code.
 *
 * There is deliberately NO input in this table: which parameters exist, what
 * type each carries and which prompt-command spellings state it are protocol
 * facts (the vocabulary version this SKU sells), a vendor generation that
 * adds one is a new version constant, and value domains are the upstream's
 * own — so there is nothing here an operator could correctly be asked to
 * type. Cutting a SKU from V1 to V2 is an API write of
 * `parameter_vocabulary`, not a row in this table.
 */
function VocabularyTable(props: {
  readonly vocabulary: readonly ArkTaskProtocolParameter[];
}): JSX.Element {
  const t = useT();
  return (
    <div className="flex flex-col gap-2" data-testid="model-protocols-vocabulary">
      <span className="text-label text-paper-mid">
        {t('model_detail.protocols.docs.vocabulary.label')}
      </span>
      <p className="m-0 text-micro text-paper-low">
        {t('model_detail.protocols.docs.vocabulary.hint')}
      </p>
      <table className="w-full max-w-xl border-collapse text-left text-micro">
        <thead>
          <tr className="text-paper-low">
            <th className="py-1 pr-4 font-normal">
              {t('model_detail.protocols.docs.vocabulary.col_parameter')}
            </th>
            <th className="py-1 pr-4 font-normal">
              {t('model_detail.protocols.docs.vocabulary.col_type')}
            </th>
            <th className="py-1 font-normal">
              {t('model_detail.protocols.docs.vocabulary.col_commands')}
            </th>
          </tr>
        </thead>
        <tbody>
          {props.vocabulary.map((parameter: ArkTaskProtocolParameter) => (
            <tr
              key={parameter.name}
              className="text-paper"
              data-testid={`model-protocols-vocabulary-${parameter.name}`}
            >
              <td className="py-1 pr-4 font-mono">{parameter.name}</td>
              <td className="py-1 pr-4">{parameter.kind}</td>
              <td className="py-1 font-mono">{parameter.commands.join(', ')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface AdmissionEditorProps {
  readonly draft: ProtocolDocsDraft;
  readonly onChange: (draft: ProtocolDocsDraft) => void;
  readonly disabled: boolean;
}

/**
 * The concurrency admission editor. The tier classes are the allowance map's
 * OWN keys (#1883): typing a key IS listing the tier — R4's "listing a tier
 * forces someone to state its exposure" — and a submitted value outside the
 * key set is refused at the route gate before any spend.
 */
function AdmissionEditor({ draft, onChange, disabled }: AdmissionEditorProps): JSX.Element {
  const t = useT();

  function updateRow(index: number, patch: Partial<AdmissionAllowanceDraft>): void {
    onChange({
      ...draft,
      allowances: draft.allowances.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    });
  }

  return (
    <div className="flex flex-col gap-3" data-testid="model-protocols-admission-editor">
      <span className="text-label text-paper-mid">
        {t('model_detail.protocols.docs.admission.label')}
      </span>
      <p className="m-0 text-micro text-paper-low">
        {t('model_detail.protocols.docs.admission.hint')}
      </p>
      <FormField
        label={t('model_detail.protocols.docs.admission.tier_class')}
        fieldId="model-protocols-tier-class"
        className="sm:max-w-xs"
      >
        <Select
          id="model-protocols-tier-class"
          value={draft.tierClassParameter.length === 0 ? undefined : draft.tierClassParameter}
          onValueChange={(next) => onChange({ ...draft, tierClassParameter: next })}
          options={arkTaskParametersForVocabulary(draft.parameterVocabulary ?? 'v1').map(
            (parameter) => ({
              value: parameter.name,
              label: parameter.name,
            }),
          )}
          placeholder={t('model_detail.protocols.docs.admission.tier_class_placeholder')}
          mono
          disabled={disabled}
          ariaLabel={t('model_detail.protocols.docs.admission.tier_class')}
          testId="model-protocols-tier-class"
        />
      </FormField>
      {draft.allowances.map((row, index) => (
        <div
          // Index keys are safe: rows are only appended/removed by explicit
          // operator action and carry no identity of their own.
          key={index}
          className="flex flex-wrap items-end gap-3"
          data-testid={`model-protocols-allowance-row-${index}`}
        >
          <FormField
            label={t('model_detail.protocols.docs.admission.tier_class_value')}
            fieldId={`model-protocols-allowance-${index}-tier`}
            className="w-40"
          >
            <Input
              id={`model-protocols-allowance-${index}-tier`}
              type="text"
              mono
              value={row.tierClass}
              onChange={(e) => updateRow(index, { tierClass: e.currentTarget.value })}
              data-testid={`model-protocols-allowance-${index}-tier`}
              disabled={disabled}
              placeholder={t('model_detail.protocols.docs.admission.tier_class_value_placeholder')}
            />
          </FormField>
          <FormField
            label={t('model_detail.protocols.docs.admission.allowance', {
              tier: row.tierClass.trim().length === 0 ? '…' : row.tierClass.trim(),
            })}
            fieldId={`model-protocols-allowance-${index}-cap`}
            className="w-32"
          >
            <Input
              id={`model-protocols-allowance-${index}-cap`}
              type="number"
              mono
              min="1"
              step="1"
              value={row.allowance}
              onChange={(e) => updateRow(index, { allowance: e.currentTarget.value })}
              data-testid={`model-protocols-allowance-${index}-cap`}
              disabled={disabled}
            />
          </FormField>
          <button
            type="button"
            onClick={() =>
              onChange({ ...draft, allowances: draft.allowances.filter((_, i) => i !== index) })
            }
            disabled={disabled || draft.allowances.length === 1}
            aria-label={t('model_detail.protocols.docs.admission.remove_aria', { row: index + 1 })}
            data-testid={`model-protocols-allowance-${index}-remove`}
            className="mb-1 inline-flex h-7 w-7 items-center justify-center rounded-full text-paper-low transition-colors duration-120 hover:text-bad focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40 disabled:opacity-40"
          >
            <X aria-hidden="true" className="h-4 w-4" />
          </button>
        </div>
      ))}
      <div>
        <Button
          type="button"
          variant="secondary"
          onClick={() =>
            onChange({ ...draft, allowances: [...draft.allowances, EMPTY_ALLOWANCE] })
          }
          disabled={disabled}
          data-testid="model-protocols-admission-add-tier"
        >
          {t('model_detail.protocols.docs.admission.add_tier')}
        </Button>
      </div>
    </div>
  );
}
